// src/components/pages/portfolios/hooks/usePortfoliosData.js
import { useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_BASE_URL;
const ITEMS_PER_PAGE = 50;
const ALLOWED_SORT_KEYS = [
    'ret', 'mdd', 'sharpe', 'calmar', 'sortino', 'vol', 'nickname',
];
const ALLOWED_DIRECTIONS = ['asc', 'desc'];
const PERIODS_KEYS = ['1w', '2w', '1m', '3m', '6m', '1y', '3y', 'all'];

// =======================================================================
// 💡 추가: 캐시 유효성 검사 함수 (매일 오전 9시 KST 기준 업데이트)
// =======================================================================
const isCacheValid = (timestamp) => {
    if (!timestamp) return false;

    const cacheTime = new Date(timestamp);
    const now = new Date();

    // 데이터 업데이트 기준 시각: 매일 09:00 KST (== 00:00 UTC)
    // 현재 시점에서의 '마지막 업데이트 시점'을 계산합니다.
    const lastUpdatePoint = new Date();
    lastUpdatePoint.setUTCHours(0, 0, 0, 0); // 오늘 00:00 UTC 설정

    // 현재 시간이 오늘 00:00 UTC(오전 9시 KST)보다 이전이라면,
    // 실제 마지막 데이터 업데이트는 '어제' 00:00 UTC에 이루어진 것입니다.
    if (now < lastUpdatePoint) {
        lastUpdatePoint.setDate(lastUpdatePoint.getDate() - 1);
    }

    // 캐시 저장 시간이 마지막 업데이트 시점보다 뒤라면 유효한(최신) 데이터입니다.
    return cacheTime > lastUpdatePoint;
};

export function usePortfoliosData(initialPeriodKey) {
  const [items, setItems] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: `ret_${initialPeriodKey}`, direction: 'desc' });
  
  const lastEvaluatedKey = useRef(null);
  const prevSortConfigRef = useRef(null);
  const observerTargetRef = useRef(null);
  const tableWrapperRef = useRef(null);

  // 💡 정렬 기준 요청 함수
  const requestSort = useCallback((baseKey) => {
    const validBaseKey = ALLOWED_SORT_KEYS.find(k => baseKey.startsWith(k));
    if (!validBaseKey) {
      console.error('Invalid sort key provided:', baseKey);
      return;
    }

    let newSortKey = validBaseKey === 'nickname' ? 'nickname' : baseKey;

    setSortConfig((prev) => {
      if (prev.key === newSortKey) {
        return { ...prev, direction: prev.direction === 'desc' ? 'asc' : 'desc' };
      }
      let newDirection = (newSortKey.startsWith('mdd_') || newSortKey.startsWith('vol_')) ? 'asc' : 'desc';
      return { key: newSortKey, direction: newDirection };
    });
  }, []);

  // 💡 데이터 페칭 로직 (캐싱 적용)
  const fetchPortfolios = useCallback(async (startKey = null, replace = false) => {
    if (tableLoading || isFetchingMore) return;

    const { key, direction } = sortConfig;
    // 캐시 키 생성 (정렬 조건별로 고유해야 함)
    const cacheKey = `pf_cache_${key}_${direction}`;

    // 1. 초기 로딩(replace=true)이고 startKey가 없을 때 캐시 확인
    if (!startKey && replace) {
        const cachedDataStr = sessionStorage.getItem(cacheKey);
        if (cachedDataStr) {
            try {
                const cached = JSON.parse(cachedDataStr);
                // 캐시가 유효(오늘 09:00 이후 저장 or 업데이트 주기 내)한지 확인
                if (isCacheValid(cached.timestamp)) {
                    console.log(`[Cache Hit] Loaded from session storage: ${cacheKey}`);
                    setItems(cached.items);
                    lastEvaluatedKey.current = cached.lastEvaluatedKey;
                    setHasMore(!!cached.lastEvaluatedKey);
                    setInitialLoading(false);
                    return; // API 호출 없이 종료
                }
            } catch (e) {
                console.warn("Cache parsing error, falling back to API");
                sessionStorage.removeItem(cacheKey);
            }
        }
    }

    // API 호출 시작
    if (startKey) {
      setIsFetchingMore(true);
    } else {
      setTableLoading(true);
    }

    try {
      const [baseKey, periodKey] = key.split('_');
      
      const isValidSortKey = key === 'nickname' || (ALLOWED_SORT_KEYS.includes(baseKey) && PERIODS_KEYS.includes(periodKey));
      const isValidDirection = ALLOWED_DIRECTIONS.includes(direction);
      
      if (!isValidSortKey || !isValidDirection) {
          console.error('Invalid sort config detected:', { key, direction });
          setSortConfig({ key: 'ret_1y', direction: 'desc' }); // 기본값 복구
          return;
      }
      
      let url = `${API_URL}/portfolios?limit=${ITEMS_PER_PAGE}&sortby=${key}&direction=${direction}&benchmark`;
      
      if (startKey) {
        // StartKey Sanitization
        const sanitizedStartKey = {};
        for (const k in startKey) {
          if (['ret_', 'mdd_', 'vol_', 'sharpe_', 'calmar_', 'sortino_'].some(prefix => k.startsWith(prefix))) {
            sanitizedStartKey[k] = parseFloat(startKey[k]);
          } else {
            sanitizedStartKey[k] = startKey[k];
          }
        }
        url += `&startKey=${encodeURIComponent(JSON.stringify(sanitizedStartKey))}`;
      }
      
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch (${res.status})`);
      
      const data = await res.json();
      const newPortfoliosWithRank = data.portfolios.map((portfolio, index) => ({
        ...portfolio,
        rank: (replace ? 0 : items.length) + index + 1,
      }));
      
      // 상태 업데이트를 위한 새로운 아이템 배열 계산
      const updatedItems = replace ? newPortfoliosWithRank : [...items, ...newPortfoliosWithRank];
      
      setItems(updatedItems);
      lastEvaluatedKey.current = data.lastEvaluatedKey;
      setHasMore(!!data.lastEvaluatedKey);

      // 💡 2. 데이터 페칭 성공 시 캐시 저장
      // 무한 스크롤로 데이터가 늘어나도 현재 상태(updatedItems) 전체를 저장하여 복구 가능하게 함
      sessionStorage.setItem(cacheKey, JSON.stringify({
          items: updatedItems,
          lastEvaluatedKey: data.lastEvaluatedKey,
          timestamp: new Date().toISOString()
      }));
      
    } catch (err) {
      console.error("Error fetching portfolios:", err);
      setHasMore(false);
    } finally {
      setInitialLoading(false);
      setTableLoading(false);
      setIsFetchingMore(false);
    }
  }, [sortConfig, items, tableLoading, isFetchingMore]); // items 의존성 포함 (캐시 저장 시 필요)
  
  // 초기 로딩
  useEffect(() => {
    if (initialLoading) {
      fetchPortfolios(null, true);
    }
  }, [initialLoading, fetchPortfolios]);

  // 정렬 변경 시 리셋 및 재페칭
  useEffect(() => {
    // 이전 설정과 비교하여 변경되었을 때만 실행
    if (prevSortConfigRef.current && (prevSortConfigRef.current.key !== sortConfig.key || prevSortConfigRef.current.direction !== sortConfig.direction)) {
      lastEvaluatedKey.current = null;
      setHasMore(true);
      fetchPortfolios(null, true);
    }
    prevSortConfigRef.current = sortConfig;
  }, [sortConfig, fetchPortfolios]);
  
  // 무한 스크롤 로직
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !tableLoading && !isFetchingMore && hasMore) {
          fetchPortfolios(lastEvaluatedKey.current, false);
        }
      },
      {
        root: tableWrapperRef.current,
        threshold: 0
      }
    );
    
    const currentTarget = observerTargetRef.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }
    
    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [tableLoading, isFetchingMore, hasMore, fetchPortfolios]);

  return {
    items,
    initialLoading,
    tableLoading,
    isFetchingMore,
    hasMore,
    sortConfig,
    requestSort,
    observerTargetRef,
    tableWrapperRef,
  };
}